import { useState, useEffect, createContext, useContext } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { ThemeProvider, LightOnlyThemeSync } from "./context/ThemeContext";
import { PermissionsProvider, usePermissions } from "./contexts/PermissionsContext";
import NewVersionPrompt from "./components/NewVersionPrompt";
import "./i18n"; // Initialize i18n

// Preload critical assets to prevent flash
const preloadImage = (src) => {
  const img = new Image();
  img.src = src;
};
preloadImage("/trp-logo.png");

// Pages
import Login from "./pages/Login";
import TodayView from "./pages/TodayView";
import ProgramsView from "./pages/ProgramsView";
import ProgramDetail from "./pages/ProgramDetail";
import CalendarView from "./pages/CalendarView";
import Meetings from "./pages/Meetings";
import OAuthComplete from "./pages/OAuthComplete";
import MoneyView from "./pages/MoneyView";
import Settings from "./pages/Settings";
import TimelineView from "./pages/TimelineView";
import TimelineSettingsPage from "./pages/TimelineSettingsPage";
import AcceptInvite from "./pages/AcceptInvite";
import WhiteboardView from "./pages/WhiteboardView";
import ArtistManagement from "./pages/ArtistManagement";
import ArtistDirectory from "./pages/ArtistDirectory";
import ArtistDetail from "./pages/ArtistDetail";
import IntakeForm from "./pages/IntakeForm";
import IntakeSubmissions from "./pages/IntakeSubmissions";
import Pipeline from "./pages/Pipeline";
import ResourceDirectory from "./pages/ResourceDirectory";
import ResourceCenter from "./pages/ResourceCenter";
import ResourceProfilePage from "./pages/ResourceProfilePage";
import ManagementView from "./pages/ManagementView";
import LeavesView from "./pages/LeavesView";
import ArtistPerformance from "./pages/ArtistPerformance";
import ExecutiveCommandCenter from "./pages/ExecutiveCommandCenter";
import VexosExecutive from "./pages/VexosExecutive";
import PlanningCommandCenter from "./pages/PlanningCommandCenter";
import YellowPages from "./pages/YellowPages";
import ProjectCostingView from "./pages/costing/ProjectCostingView";
import ScopeConfirmation from "./pages/ScopeConfirmation";
import OTLogger from "./pages/OTLogger";
import VendorManagement from "./pages/VendorManagement";
import ArchVizPlanner from "./pages/ArchVizPlanner";
import ReviewStudio from "./pages/ReviewStudio";
import HelpDesk from "./pages/HelpDesk";
import ReviewSessionDetail from "./pages/ReviewSessionDetail";
import ClientReview from "./pages/ClientReview";

// Artist Portal Pages
import ArtistProjects from "./pages/artist/ArtistProjects";
import ArtistTimeline from "./pages/artist/ArtistTimeline";
import ArtistTimeOff from "./pages/artist/ArtistTimeOff";
import ArtistProfile from "./pages/artist/ArtistProfile";

// Admin Pages
import UserManagement from "./pages/admin/UserManagement";
import RolesPermissions from "./pages/admin/RolesPermissions";
import AuditLog from "./pages/admin/AuditLog";
import ActivityLogPage from "./pages/ActivityLogPage";

// Smart Orange
import SmartOrange from "./pages/SmartOrange";

// Chat
import ChatView from "./pages/chat/ChatView";

// AI Workflows
import WorkflowsListSimple from "./pages/ai-workflows/WorkflowsListSimple";
import VisualCanvasBoardWrapper from "./pages/ai-workflows/VisualCanvasBoardWrapper";

// P&L View
import PnLView from "./pages/PnLView";

// Calculators
import CalculatorsDashboard from "./pages/calculators/CalculatorsDashboard";
import ChangeOrdersDashboard from "./pages/ChangeOrdersDashboard";
import ClientApprovalPage from "./pages/ClientApprovalPage";
import UpsellsDashboard from "./pages/UpsellsDashboard";
import UpsellOpportunities from "./pages/UpsellOpportunities";
import UpsellProposalPrint from "./pages/UpsellProposalPrint";
import UpsellCatalogAdminPage from "./pages/UpsellCatalogAdminPage";
import FathomCallsDashboard from "./pages/FathomCallsDashboard";
import FathomCallDetail from "./pages/FathomCallDetail";
import FathomSettings from "./pages/FathomSettings";
import SalesPipelineSync from "./pages/SalesPipelineSync";
import ResourcesLibrary from "./pages/ResourcesLibrary";
import AIContentCalculator from "./pages/calculators/AIContentCalculator";
import AIContentAdmin from "./pages/calculators/AIContentAdmin";
import VexosCalculator from "./pages/calculators/VexosCalculator";
import VisualizationCalculator from "./pages/calculators/VisualizationCalculator";
import PricingAdmin from "./pages/calculators/PricingAdmin";
import CombinedCalculator from "./pages/calculators/CombinedCalculator";
import VexosClientCalculator from "./pages/calculators/VexosClientCalculator";
import VexosSavedQuotes from "./pages/calculators/VexosSavedQuotes";
import QuoteLinkManager from "./pages/QuoteLinkManager";
import ProposalPage from "./pages/calculators/ProposalPage";import MarketingCalculator from "./pages/calculators/MarketingCalculator";
import MarketingCollateralCalculator from "./pages/calculators/MarketingCollateralCalculator";
import SavedQuotes from "./pages/calculators/SavedQuotes";

// Portfolio Pages
import PortfolioGallery from "./pages/portfolio/PortfolioGallery";
import PortfolioProjectPage from "./pages/portfolio/PortfolioProjectPage";
import PortfolioAdd from "./pages/portfolio/PortfolioAdd";
import { PORTFOLIO_CURATOR_ROLES } from "./components/portfolio/scopes";

// VEXOS Client Portal
import VexosClientPortal from "./pages/VexosClientPortal";
import ClientTracker from "./pages/ClientTracker";
import ProjectTrackerManager from "./pages/ProjectTrackerManager";
import TrackerAdmin from "./pages/TrackerAdmin";

// Delivery Module
import DeliveryManager from "./pages/DeliveryManager";
import DeliveryClient from "./pages/DeliveryClient";
import StrategyCallDeposit from "./pages/StrategyCallDeposit";

// Financials
import FinancialsView from "./pages/financials/FinancialsView";

const FINANCIALS_ALLOWED_EMAILS = [
  "bernard@therenderpros.com",
  "cole@therenderpros.com",
  "juanpotasman2003@gmail.com",
];

// Layout
import Layout from "./components/Layout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

// PM Test Mode Context (for Admin impersonation)
const PmTestModeContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const usePmTestMode = () => {
  const context = useContext(PmTestModeContext);
  return context; // Returns null if not in context, which is fine
};

// Re-export usePermissions for convenience
export { usePermissions } from "./contexts/PermissionsContext";

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      } catch (error) {
        localStorage.removeItem("token");
        setToken(null);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    verifyToken();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (email, password, name) => {
    const response = await axios.post(`${API}/auth/register`, { 
      email, 
      password, 
      name,
      role: "COO"
    });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!user
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route Component with Permissions
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return (
    <PermissionsProvider user={user}>
      {children}
    </PermissionsProvider>
  );
};

// Role-Protected Route Component - Restricts access based on user role
const RoleProtectedRoute = ({ children, allowedRoles, deniedRoles }) => {
  const { user } = useAuth();
  
  // If deniedRoles specified, check if user's role is denied
  if (deniedRoles && deniedRoles.includes(user?.role)) {
    return <Navigate to="/" replace />;
  }
  
  // If allowedRoles specified, check if user's role is allowed
  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Single-purpose logins. Each of these roles may only visit the handful of
// paths listed here; a whitelist (rather than deniedRoles on every route) means
// new routes are closed to these roles by default. `allowed` entries match the
// path exactly or as a prefix; `denied` entries are exact paths carved back out
// of an allowed prefix. Everyone else passes through untouched.
const SCOPED_ROLES = {
  "VEXOS Executive": {
    allowed: ["/vexos-executive", "/portfolio", "/settings", "/help-desk"],
    home: "/vexos-executive",
  },
  // Sales-rep assistant: builds quotes in the Client Calculator and preps
  // upsells. /upsells is allowed as a prefix so the opportunities list and the
  // printable proposal come along, but the catalog *price* admin is carved back
  // out — the backend rejects his edits there anyway (Admin/Finance only).
  "Sales Assistant": {
    allowed: ["/sales/client-calculator", "/upsells", "/settings", "/help-desk"],
    denied: ["/upsells/catalog-admin"],
    home: "/sales/client-calculator",
  },
};
const ScopedRoleGuard = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const scope = SCOPED_ROLES[user?.role];
  if (scope) {
    const path = location.pathname;
    const allowed =
      !(scope.denied || []).includes(path) &&
      scope.allowed.some((p) => path === p || path.startsWith(p + "/"));
    if (!allowed) {
      return <Navigate to={scope.home} replace />;
    }
  }
  return children;
};

const EmailProtectedRoute = ({ children, allowedEmails }) => {
  const { user } = useAuth();
  const email = (user?.email || "").toLowerCase();
  if (!allowedEmails.map(e => e.toLowerCase()).includes(email)) {
    return <Navigate to="/" replace />;
  }
  return children;
};

// API Helper with auth
export const apiClient = axios.create({
  baseURL: API
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect on blob response errors (these are handled locally)
    if (error.config?.responseType === 'blob') {
      return Promise.reject(error);
    }
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <AuthProvider>
          <LightOnlyThemeSync />
          <Toaster position="top-right" richColors />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/invite/:token" element={<AcceptInvite />} />
            {/* Zoom / Outlook OAuth popup landing — chrome-free so it can self-close */}
            <Route path="/oauth-complete" element={<OAuthComplete />} />
            {/* VEXOS Client Portal - Public, token-based access */}
            <Route path="/client/vexos-setup/:token" element={<VexosClientPortal />} />
            {/* Delivery Client Portal - Public, token-based access */}
            <Route path="/delivery/:token" element={<DeliveryClient />} />
            {/* Client Project Tracker - Public, token-based access (Domino's-style) */}
            <Route path="/track/:token" element={<ClientTracker />} />
            {/* Review Studio Client Portal - Public, token-based access */}
            <Route path="/review/:token" element={<ClientReview />} />
            {/* Change Order / Upsell Client Approval Portal - Public, token-based */}
            <Route path="/approval/:token" element={<ClientApprovalPage />} />
            {/* Strategy Call deposit — Public, token-based. Card carries a 3%
                convenience fee, wire does not, so the client picks here. */}
            <Route path="/strategy-call/:token" element={<StrategyCallDeposit />} />
            {/* VEXOS Proposal & Agreement — auth-protected, standalone (no app chrome, printable). */}
            <Route
              path="/proposals/:id"
              element={
                <ProtectedRoute>
                  <ProposalPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <Layout>
                    {/* Staff-only: client portals must never be told to refresh. */}
                    <NewVersionPrompt />
                    <ScopedRoleGuard>
                    <Routes>
                      <Route path="/" element={<TodayView />} />
                      {/* The PM Dashboard was removed (2026-08-10) — unused, and
                          it never had a sidebar entry. There is no catch-all
                          route, so without this an old bookmark would render the
                          chrome around an empty page. */}
                      <Route path="/pm-dashboard" element={<Navigate to="/" replace />} />
                      <Route path="/programs" element={<ProgramsView />} />
                      <Route path="/programs/:id" element={<ProgramDetail />} />
                      <Route path="/programs/:id/timeline" element={<TimelineView />} />
                      <Route path="/programs/:programId/delivery" element={<DeliveryManager />} />
                      <Route path="/programs/:id/tracker" element={<ProjectTrackerManager />} />
                      <Route path="/whiteboard" element={<WhiteboardView />} />
                      <Route path="/calendar" element={<CalendarView />} />
                      <Route path="/meetings" element={<Meetings />} />
                      <Route path="/money" element={<MoneyView />} />
                      {/* Redirect old artist directory to resource center */}
                      <Route path="/studio/artists/directory" element={<Navigate to="/studio/resource-center" replace />} />
                      <Route path="/studio/artists/directory/:resourceId" element={
                        <RoleProtectedRoute deniedRoles={["Program Manager", "Marketing PM"]}>
                          <ResourceProfilePage />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/studio/artists/timeline" element={<ArtistManagement />} />
                      <Route path="/studio/timeline-settings" element={
                        <RoleProtectedRoute deniedRoles={["Program Manager", "Marketing PM", "Sales", "Production"]}>
                          <TimelineSettingsPage />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/studio/artists" element={<Navigate to="/studio/resource-center" replace />} />
                      <Route path="/sales/intake/new" element={<IntakeForm />} />
                      <Route path="/sales/intake/submissions" element={<IntakeSubmissions />} />
                      {/* Calculator Routes */}
                      <Route path="/sales/calculators" element={<CalculatorsDashboard />} />
                      <Route path="/sales/calculators/vexos/:quoteId" element={<VexosCalculator />} />
                      <Route path="/sales/calculators/visualization/:quoteId" element={<VisualizationCalculator />} />
                      <Route path="/sales/calculators/combined/:quoteId" element={<CombinedCalculator />} />
                      <Route path="/sales/calculators/marketing/:quoteId" element={<MarketingCalculator />} />
                      <Route path="/sales/calculators/collateral/:quoteId" element={<MarketingCollateralCalculator />} />
                      <Route path="/sales/calculators/saved" element={<SavedQuotes />} />
                      <Route path="/change-orders" element={<ChangeOrdersDashboard />} />
                      <Route path="/upsells" element={<UpsellsDashboard />} />
                      {/* Recommendations (what to propose) — distinct from
                          /upsells, which is the pipeline for what was proposed. */}
                      <Route path="/upsells/opportunities" element={<UpsellOpportunities />} />
                      <Route path="/upsells/catalog-admin" element={<UpsellCatalogAdminPage />} />
                      <Route path="/upsells/:coId/proposal" element={<UpsellProposalPrint />} />
                      <Route path="/fathom/calls" element={<FathomCallsDashboard />} />
                      <Route path="/fathom/calls/:callId" element={<FathomCallDetail />} />
                      <Route path="/fathom/settings" element={<FathomSettings />} />
                      <Route path="/sales-pipeline" element={<SalesPipelineSync />} />
                      <Route path="/resources" element={<ResourcesLibrary />} />
                      <Route path="/sales/calculators/ai-content/admin" element={<AIContentAdmin />} />
                      <Route path="/sales/calculators/ai-content/:quoteId" element={<AIContentCalculator />} />
                      <Route path="/sales/calculators/ai-content" element={<AIContentCalculator />} />
                      <Route path="/sales/calculators/pricing-admin" element={
                        <RoleProtectedRoute allowedRoles={["COO", "Admin"]}>
                          <PricingAdmin />
                        </RoleProtectedRoute>
                      } />
                      {/* Pipeline - merged Awaiting Activation + Activated Projects */}
                      <Route path="/ops/pipeline" element={<Pipeline />} />
                      {/* VEXOS Client-Facing Sales Calculator. The /sales/client-calculator
                          path is the Saved Quotes dashboard (landing). The form lives
                          at /new for blank starts and /:id for editing a saved quote. */}
                      <Route path="/sales/client-calculator" element={<VexosSavedQuotes />} />
                      <Route path="/sales/client-calculator/new" element={<VexosClientCalculator />} />
                      <Route path="/sales/client-calculator/:quoteId" element={<VexosClientCalculator />} />
                      {/* Quote ⇄ Project linking (backfill tool) — connect activated
                          programs back to their originating Client-Calculator quote. */}
                      <Route path="/sales/quote-links" element={<QuoteLinkManager />} />
                      {/* Legacy redirects */}
                      <Route path="/ops/programs/awaiting-activation" element={<Navigate to="/ops/pipeline?tab=awaiting" replace />} />
                      <Route path="/ops/programs/activated" element={<Navigate to="/ops/pipeline?tab=active" replace />} />
                      <Route path="/studio/resource-center" element={<ResourceCenter />} />
                      <Route path="/studio/resources" element={<ResourceCenter />} />
                      <Route path="/studio/resources/new" element={<ResourceProfilePage />} />
                      <Route path="/studio/resources/:resourceId" element={<ResourceProfilePage />} />
                      <Route path="/studio/management" element={<ManagementView />} />
                      <Route path="/studio/leaves" element={<LeavesView />} />
                      <Route path="/studio/performance" element={
                        <RoleProtectedRoute deniedRoles={["Program Manager", "Marketing PM"]}>
                          <ArtistPerformance />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/studio/yellow-pages" element={<YellowPages />} />
                      <Route path="/studio/ot-logger" element={<OTLogger />} />
                      <Route path="/studio/vendors" element={<VendorManagement />} />
                      <Route path="/studio/production-planner" element={<Navigate to="/programs" replace />} />
                      <Route path="/studio/planners" element={<Navigate to="/programs" replace />} />
                      <Route path="/studio/planners/:plannerId" element={<Navigate to="/programs" replace />} />
                      <Route path="/archviz-planner" element={<ArchVizPlanner />} />
                      <Route path="/review-studio" element={<ReviewStudio />} />
                      <Route path="/review-studio/:sessionId" element={<ReviewSessionDetail />} />
                      <Route path="/studio/project-costing" element={
                        <RoleProtectedRoute allowedRoles={["COO", "Admin", "Sales", "Accountant"]}>
                          <ProjectCostingView />
                        </RoleProtectedRoute>
                      } />
                      <Route path="/executive-command-center" element={<ExecutiveCommandCenter />} />
                    <Route path="/command-center" element={<ExecutiveCommandCenter />} />
                    <Route path="/planning-command-center/*" element={<PlanningCommandCenter />} />
                    {/* Redirect legacy executive management route to studio management */}
                    <Route path="/executive/management" element={<Navigate to="/studio/management" replace />} />
                    <Route path="/pnl" element={
                      <RoleProtectedRoute allowedRoles={["COO", "Admin", "Accountant"]}>
                        <PnLView />
                      </RoleProtectedRoute>
                    } />
                    <Route path="/vexos-executive" element={
                      <RoleProtectedRoute allowedRoles={["COO", "Admin", "Accountant", "VEXOS Executive"]}>
                        <VexosExecutive />
                      </RoleProtectedRoute>
                    } />
                    <Route path="/programs/:id/confirm-scope" element={
                      <RoleProtectedRoute allowedRoles={["COO", "Admin"]}>
                        <ScopeConfirmation />
                      </RoleProtectedRoute>
                    } />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/help-desk" element={<HelpDesk />} />
                    
                    {/* Artist Portal Routes */}
                    <Route path="/artist/projects" element={<ArtistProjects />} />
                    <Route path="/artist/timeline" element={<ArtistTimeline />} />
                    <Route path="/artist/time-off" element={<ArtistTimeOff />} />
                    <Route path="/artist/profile" element={<ArtistProfile />} />
                    
                    {/* Portfolio Routes */}
                    <Route path="/portfolio" element={<PortfolioGallery />} />
                    <Route path="/portfolio/add" element={
                      <RoleProtectedRoute allowedRoles={PORTFOLIO_CURATOR_ROLES}>
                        <PortfolioAdd />
                      </RoleProtectedRoute>
                    } />
                    {/* One page for both audiences — /edit just opens it in organize mode */}
                    <Route path="/portfolio/:id" element={<PortfolioProjectPage />} />
                    <Route path="/portfolio/:id/edit" element={
                      <RoleProtectedRoute allowedRoles={PORTFOLIO_CURATOR_ROLES}>
                        <PortfolioProjectPage />
                      </RoleProtectedRoute>
                    } />

                    {/* Financials - Executive Financial Command Center */}
                    <Route path="/financials" element={
                      <EmailProtectedRoute allowedEmails={FINANCIALS_ALLOWED_EMAILS}>
                        <FinancialsView />
                      </EmailProtectedRoute>
                    } />
                    
                    {/* Admin Routes */}
                    <Route path="/admin/users" element={
                      <RoleProtectedRoute allowedRoles={["COO", "Admin"]}>
                        <UserManagement />
                      </RoleProtectedRoute>
                    } />
                    <Route path="/admin/roles" element={
                      <RoleProtectedRoute allowedRoles={["COO", "Admin"]}>
                        <RolesPermissions />
                      </RoleProtectedRoute>
                    } />
                    <Route path="/admin/audit" element={
                      <RoleProtectedRoute allowedRoles={["COO", "Admin"]}>
                        <AuditLog />
                      </RoleProtectedRoute>
                    } />
                    <Route path="/admin/activity-log" element={
                      <RoleProtectedRoute allowedRoles={["COO", "Admin"]}>
                        <ActivityLogPage />
                      </RoleProtectedRoute>
                    } />
                    {/* The console behind the Client Tracker: email flows, the
                        copy, the record of what went out, and the arm switch.
                        Gated here AND in the router — this one decides what
                        every client is told. */}
                    <Route path="/admin/tracker" element={
                      <RoleProtectedRoute allowedRoles={["COO", "Admin"]}>
                        <TrackerAdmin />
                      </RoleProtectedRoute>
                    } />
                    
                    {/* Smart Orange - COO/Admin only */}
                    <Route path="/smart-orange" element={
                      <RoleProtectedRoute allowedRoles={["COO", "Admin"]}>
                        <SmartOrange />
                      </RoleProtectedRoute>
                    } />
                    
                    {/* TRP Chat */}
                    <Route path="/chat" element={<ChatView />} />
                    <Route path="/chat/:channelId" element={<ChatView />} />
                    
                    {/* AI Workflows */}
                    <Route path="/ai-workflows" element={<WorkflowsListSimple />} />
                    <Route path="/ai-workflows/new" element={<VisualCanvasBoardWrapper />} />
                    <Route path="/ai-workflows/:workflowId" element={<VisualCanvasBoardWrapper />} />
                  </Routes>
                    </ScopedRoleGuard>
                </Layout>
              </ProtectedRoute>
            }
          />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
